import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Image, Group, Rect, Circle } from 'react-konva';

import { DesignElement } from '../types/index';
import { canvasLoadStart, canvasLoadDone } from '../utils/canvasLoadingCounter';

interface VaseElementProps {
    el: DesignElement;
    stageSize: { width: number; height: number };
    graniteTemplate: any;
    compositeProps: any;
    pixelsPerInch: number;
    isSerptopRightOnly?: boolean;
    selectedArtworkId: string | null;
    onSelect: (id: string) => void;
    updateDesign: (updater: (prev: any) => any) => void;
    imageCache: Map<string, HTMLImageElement>;
    onAssetLoaded?: () => void;
    onRightClick?: (e: any, id: string) => void;
}

const VaseElement: React.FC<VaseElementProps> = ({
    el,
    stageSize,
    graniteTemplate,
    compositeProps,
    pixelsPerInch,
    isSerptopRightOnly = false,
    selectedArtworkId,
    onSelect,
    updateDesign,
    imageCache,
    onAssetLoaded,
    onRightClick
}) => {
    const [img, setImg] = useState<HTMLImageElement | null>(() => {
        const src = el.properties?.vaseSrcOriginal || el.properties?.src;
        return src ? imageCache.get(src) || null : null;
    });

    const isSelected = selectedArtworkId === el.id;
    const imageRef = useRef<any>(null);

    // 1. Determine Variant (used for side/position logic)
    const variant = String(el.properties?.vaseVariantId || 'AV01').trim().toUpperCase();
    const isAV02 = variant === 'AV02'; // kept for any future variant-specific logic

    // 2. Compute Size — height is always 13.5 inches; width is proportional to the
    //    loaded image's natural dimensions. Returns null until image is ready.
    const displaySize = useMemo(() => {
        if (!graniteTemplate || !pixelsPerInch || pixelsPerInch <= 0) return null;
        if (!img || !img.naturalWidth || !img.naturalHeight) return null;
        
        let vaseHeight = Math.max(20, Math.floor(13.5 * pixelsPerInch));
        
        // Keep vase sizing consistent across modes (LSP/QD and ESQ) by relying on the same
        // 13.5" physical height derived from PPI. Any further visual tuning should happen
        // via correct ESQ modal inches/PPI, not a hard-coded downscale.

        const ratio = img.naturalWidth / img.naturalHeight;
        const vaseWidth = Math.max(1, Math.round(vaseHeight * ratio));
        return { width: vaseWidth, height: vaseHeight };
    }, [graniteTemplate, pixelsPerInch, img]);

    // 3. Compute Position — returns null if base bounds are not ready (no fallback)
    const displayPosition = useMemo(() => {
        if (!displaySize) return null;
        const base = compositeProps?.base;
        if (!base || !isFinite(base.x) || !isFinite(base.y) || !isFinite(base.width) || !isFinite(base.height)) {
            return null;
        }

        const side = isSerptopRightOnly
            ? 'right'
            : String(el.properties?.side || 'left').toLowerCase();

        let vaseX: number;
        if (side === 'right') {
            vaseX = Number(base.x) + Number(base.width) - displaySize.width;
        } else {
            vaseX = Number(base.x);
        }

        // Vase bottom aligns exactly with the top of the base (preview_img) — zero overlap
        const vaseY = Number(base.y) - displaySize.height;

        // Clamp horizontally within base bounds
        vaseX = Math.max(Number(base.x), Math.min(Number(base.x) + Number(base.width) - displaySize.width, vaseX));
        // Clamp within stage
        vaseX = Math.max(0, Math.min(stageSize.width - displaySize.width, vaseX));

        return { x: vaseX, y: vaseY };
    }, [displaySize, el.properties?.side, isSerptopRightOnly, compositeProps, stageSize.width]);

    // 4. Image Loading — must be called before any early return (Rules of Hooks)
    useEffect(() => {
        const src = el.properties?.vaseSrcOriginal || el.properties?.src;
        if (!src) return;

        // Instant cache hit — no network load, nothing to track
        if (imageCache.has(src)) {
            const cached = imageCache.get(src)!;
            if (img !== cached) setImg(cached);
            return;
        }

        // Track this load in the toolbar progress bar
        let loadCompleted = false;
        const markDone = () => {
            if (!loadCompleted) {
                loadCompleted = true;
                canvasLoadDone();
            }
        };
        canvasLoadStart();

        const image = new window.Image();
        image.crossOrigin = 'anonymous';
        if ('fetchPriority' in image) {
            (image as any).fetchPriority = 'high';
        }
        image.onload = () => {
            imageCache.set(src, image);
            setImg(image);
            markDone();
            onAssetLoaded?.();
        };
        image.onerror = () => {
            console.error(`[VaseLoad] Failed: ${src}`);
            markDone(); // balance counter even on error
        };
        image.src = src;

        return () => {
            try { image.onload = null as any; image.onerror = null as any; } catch { }
            markDone(); // balance counter if component unmounts mid-load
        };
    }, [el.properties?.src, el.properties?.vaseSrcOriginal, imageCache, onAssetLoaded, img]);

    // Robust size/position fallback to prevent vases from "disappearing" during
    // model/color switches when compositeProps might be momentarily null.
    const effectiveSize = displaySize || { width: (el.width || 100), height: (el.height || 200) };
    const effectivePosition = displayPosition || { x: el.x, y: el.y };

    // Only return null if we truly have no image and no size data at all.
    if (!img && !el.properties?.src) return null;

    const finalX = effectivePosition.x;
    const finalY = effectivePosition.y;

    // 5. Interaction Handlers
    const handleSelect = (e: any) => {
        e.cancelBubble = true;
        onSelect(el.id);
    };

    const handleDragEnd = (e: any) => {
        const node = e.target;
        // Konva Image is offset-centered; convert back to top-left corner for storage.
        const newX = node.x() - effectiveSize.width / 2;
        const newY = node.y() - effectiveSize.height / 2;
        updateDesign(prev => ({
            ...prev,
            elements: prev.elements.map((item: any) =>
                item.id === el.id ? { ...item, x: newX, y: newY } : item
            )
        }));
    };

    // 6. Render
    return (
        <Group
            onClick={handleSelect}
            onTap={handleSelect}
            onContextMenu={(e) => onRightClick?.(e, el.id)}
        >
            <Image
                ref={imageRef}
                id={el.id}
                x={finalX + effectiveSize.width / 2}
                y={finalY + effectiveSize.height / 2}
                width={effectiveSize.width}
                height={effectiveSize.height}
                offsetX={effectiveSize.width / 2}
                offsetY={effectiveSize.height / 2}
                image={img || undefined}
                draggable={false}
                onDragEnd={handleDragEnd}
                perfectDrawEnabled={false}
            />

            {isSelected && (
                <Group x={finalX} y={finalY}>
                    <Rect
                        width={effectiveSize.width}
                        height={effectiveSize.height}
                        stroke="#5d722c"
                        strokeWidth={2}
                        listening={false}
                    />
                    {[
                        { x: 0, y: 0 },
                        { x: effectiveSize.width, y: 0 },
                        { x: 0, y: effectiveSize.height },
                        { x: effectiveSize.width, y: effectiveSize.height }
                    ].map((pos, i) => (
                        <Circle
                            key={i}
                            x={pos.x}
                            y={pos.y}
                            radius={5}
                            fill="#5d722c"
                            stroke="white"
                            strokeWidth={1.5}
                            listening={false}
                        />
                    ))}
                </Group>
            )}
        </Group>
    );
};

export default React.memo(VaseElement);
